import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, AlertCircle, Clock, Landmark, Loader2 } from "lucide-react";
import {
  useWorkspaceBilling,
  useLatestAutoReloadFailure,
  useUpdateWorkspaceAutoReload,
  useEnableAutoReloadWithMandate,
  useDailySpendSuggestion,
  useCanManageBilling,
  isIndiaCard,
  formatLandsBy,
  INDIA_AUTO_RELOAD_CAP,
  PHASE_LABEL,
  type AutoReloadFailure,
  type AutoReloadSettingsInput,
  type WorkspaceBilling,
} from "@/hooks/use-workspace-billing";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { formatMoney } from "@/lib/money";

// Mirrors the server-side validation of the `billing` function's PUT auto-reload.
const THRESHOLD_MIN = 5;
const THRESHOLD_MAX = 500;
const AMOUNT_MIN = 20;
const AMOUNT_MAX = 1000;

const AMBER_ALERT = "border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50";
const AMBER_TEXT = "text-amber-700 dark:text-amber-300";
const AMBER_ICON = "h-4 w-4 text-amber-600 dark:text-amber-400";

// Decline codes that mean the bank-side authorisation is missing or dead.
const MANDATE_DECLINE_CODES = [
  "mandate_required",
  "india_recurring_payment_mandate_canceled",
  "payment_intent_mandate_invalid",
];

const inRange = (value: number, min: number, max: number) =>
  Number.isFinite(value) && value >= min && value <= max;

/**
 * On only with a card to charge. A row can say enabled with no card (the old
 * default turned it on before any card existed), and that is not auto-reload.
 */
const autoReloadOn = (billing: WorkspaceBilling) => billing.auto_reload_enabled && billing.payment_method_added;

const isMandateFailure = (failure: AutoReloadFailure) =>
  !!failure.declineCode && MANDATE_DECLINE_CODES.includes(failure.declineCode);

const failureMessage = (failure: AutoReloadFailure) => {
  const date = new Date(failure.occurredAt).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const attempt = !failure.disabled && failure.attempt ? ` (attempt ${failure.attempt} of 3)` : "";

  if (isMandateFailure(failure)) {
    return "Auto-reload is off: your card needs a bank authorisation. Turn auto-reload on to verify it (takes under a minute).";
  }
  if (failure.declineCode === "transaction_not_approved") {
    return `Your bank did not approve the last reload notice${attempt}. Approve the next one, or add funds now.`;
  }
  if (failure.disabled) {
    return `Auto-reload was disabled on ${date} because: ${failure.reason} Update your card or contact your bank, then re-enable.`;
  }
  return `The last automatic reload failed on ${date}${attempt}: ${failure.reason} Auto-reload turns off after three failures in a row.`;
};

const mandateChip = (status: WorkspaceBilling["stripe_mandate_status"], brand: string | null) => {
  const brandSuffix = brand ? ` · ${brand.charAt(0).toUpperCase()}${brand.slice(1)}` : "";
  if (status === "active") {
    return (
      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800">
        Bank authorisation active{brandSuffix}
      </Badge>
    );
  }
  if (status === "pending") {
    return (
      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/50 dark:text-blue-300 dark:border-blue-800">
        Bank authorisation activating (usually a few minutes)
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800">
      Bank authorisation needed
    </Badge>
  );
};

interface AutoReloadSettingsProps {
  /** Opens the wallet top-up dialog, optionally with an amount pre-filled. */
  onAddFunds: (presetAmount?: number) => void;
}

export function AutoReloadSettings({ onAddFunds }: AutoReloadSettingsProps) {
  const { toast } = useToast();
  const { data: profile, refetch } = useWorkspaceBilling();
  const canManage = useCanManageBilling();
  const { data: lastFailure } = useLatestAutoReloadFailure();
  const updateAutoReload = useUpdateWorkspaceAutoReload();
  const enableAutoReload = useEnableAutoReloadWithMandate();
  const isIndia = isIndiaCard(profile);
  const spend = useDailySpendSuggestion(isIndia);
  const [enabled, setEnabled] = useState(profile ? autoReloadOn(profile) : false);
  const [threshold, setThreshold] = useState(profile?.auto_reload_threshold_credits.toString() ?? "20");
  const [amount, setAmount] = useState(profile?.auto_reload_amount_credits.toString() ?? "50");
  const [belowThresholdPrompt, setBelowThresholdPrompt] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Sync state when profile updates
  useEffect(() => {
    if (profile) {
      setEnabled(autoReloadOn(profile));
      setThreshold(profile.auto_reload_threshold_credits.toString());
      setAmount(profile.auto_reload_amount_credits.toString());
      if (!autoReloadOn(profile)) setBelowThresholdPrompt(false);
    }
  }, [profile]);

  if (!profile) {
    return null;
  }

  const hasSavedCard = profile.payment_method_added;
  // India: each reload is capped by the bank authorisation it was registered with.
  const cap = isIndia ? profile.auto_reload_mandate_amount_credits ?? INDIA_AUTO_RELOAD_CAP : AMOUNT_MAX;
  const thresholdValue = parseFloat(threshold);
  const amountValue = parseFloat(amount);
  const thresholdValid = inRange(thresholdValue, THRESHOLD_MIN, THRESHOLD_MAX);
  const amountValid = inRange(amountValue, AMOUNT_MIN, cap);
  // The server's own words for what it would refuse, shown from the first Save press.
  const thresholdError =
    submitted && !thresholdValid ? `Threshold must be between $${THRESHOLD_MIN} and $${THRESHOLD_MAX}.` : null;
  const amountError =
    submitted && !amountValid
      ? isIndia
        ? `Reload amount must be between $${AMOUNT_MIN} and $${cap} for cards issued in India (bank authorisation limit).`
        : `Reload amount must be between $${AMOUNT_MIN} and $${AMOUNT_MAX}.`
      : null;
  const isBusy = updateAutoReload.isPending || enableAutoReload.isPending;
  const phaseLabel = enableAutoReload.phase
    ? PHASE_LABEL[enableAutoReload.phase]
    : updateAutoReload.isPending
      ? PHASE_LABEL.saving
      : null;
  // The server never suggests more than a save takes.
  const suggestedThreshold = spend.suggestedThreshold;

  const mandateStatus = profile.stripe_mandate_status;
  const mandateUsable = mandateStatus === "active" || mandateStatus === "pending";

  // Shown even when the switch is off: a reload the bank already holds still lands.
  const pending = profile.auto_reload_pending
    ? {
        amount: formatMoney(profile.auto_reload_pending_amount_credits ?? profile.auto_reload_amount_credits),
        landsBy: formatLandsBy(profile.auto_reload_pending_completes_at, isIndia),
        approval: !!profile.auto_reload_pending_approval_requested,
      }
    : null;

  // A mandate failure is stale once the card is verified again and auto-reload is back on.
  // Strikes 1–2 are surfaced for India cards only (each costs them a day); other
  // cards keep today's behaviour and only see the "disabled" notice.
  const failure =
    lastFailure &&
      !(isMandateFailure(lastFailure) && autoReloadOn(profile) && mandateUsable) &&
      (lastFailure.disabled || isIndia)
      ? lastFailure
      : null;

  // Enabling always goes through the mandate-aware hook: non-India cards take
  // the first call and return; India cards run the bank verification first.
  // The switch only flips once the server has confirmed.
  const enable = (settings: Omit<AutoReloadSettingsInput, "auto_reload_enabled">, clampedToCap: boolean) => {
    enableAutoReload.mutate(settings, {
      onSuccess: () => {
        setEnabled(true);
        if (clampedToCap) {
          toast({
            title: `Reload amount set to $${cap}`,
            description: "That is the bank authorisation limit for cards issued in India.",
          });
        }
        const effectiveThreshold = settings.auto_reload_threshold_credits ?? profile.auto_reload_threshold_credits;
        setBelowThresholdPrompt(isIndia && profile.wallet_balance_credits < effectiveThreshold);
      },
    });
  };

  const handleToggle = (next: boolean) => {
    if (!next) {
      updateAutoReload.mutate(
        { auto_reload_enabled: false },
        { onSuccess: () => { setEnabled(false); setBelowThresholdPrompt(false); } },
      );
      return;
    }
    const clampToCap = isIndia && profile.auto_reload_amount_credits > cap;
    enable(clampToCap ? { auto_reload_amount_credits: cap } : {}, clampToCap);
  };

  const handleSave = async () => {
    setSubmitted(true);
    if (!thresholdValid || !amountValid) return;

    const settings = {
      auto_reload_threshold_credits: thresholdValue,
      auto_reload_amount_credits: amountValue,
    };
    try {
      await updateAutoReload.mutateAsync(settings);
      await refetch();
    } catch (error) {
      // Saving amounts on an enabled row that has no bank authorisation yet:
      // run the verification with these amounts. Other errors are toasted by the hook.
      if ((error as { code?: string })?.code === "MANDATE_REQUIRED") {
        enable(settings, false);
      }
    }
  };

  return (
    <div className="space-y-6">
      {!hasSavedCard && (
        <Alert className={AMBER_ALERT}>
          <AlertCircle className={AMBER_ICON} />
          <AlertDescription className={AMBER_TEXT}>
            Add a payment method to enable auto-reload.
          </AlertDescription>
        </Alert>
      )}

      {pending && (
        <Alert className={pending.approval ? AMBER_ALERT : undefined}>
          <Clock className={pending.approval ? AMBER_ICON : "h-4 w-4"} />
          <AlertDescription className={`space-y-3 ${pending.approval ? AMBER_TEXT : ""}`}>
            <p>
              Automatic reload of {pending.amount} requested from your bank. The bank completes it
              after {pending.landsBy}; the credit appears as soon as Stripe confirms, usually within the hour.
              It can't be cancelled and is separate from any funds you add yourself.
            </p>
            {pending.approval && (
              <p className="font-medium">
                Your bank needs your approval for this reload — approve the notice it sent you
                before {pending.landsBy}, or it won't go through.
              </p>
            )}
            {canManage && (
              <Button variant="outline" size="sm" onClick={() => onAddFunds(profile.auto_reload_amount_credits)}>
                Add funds now
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      {failure && (
        <Alert className={AMBER_ALERT}>
          <AlertCircle className={AMBER_ICON} />
          <AlertDescription className={`space-y-3 ${AMBER_TEXT}`}>
            <p>{failureMessage(failure)}</p>
            {canManage && failure.declineCode === "transaction_not_approved" && (
              <Button variant="outline" size="sm" onClick={() => onAddFunds(profile.auto_reload_amount_credits)}>
                Add funds now
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
          <RefreshCw className="w-5 h-5 text-muted-foreground" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">Auto-Reload</Label>
              <p className="text-sm text-muted-foreground mt-0.5">
                {!hasSavedCard
                  ? "Requires a saved payment method"
                  : isIndia && !mandateUsable
                    ? "Cards issued in India need a one-time bank authorisation. Turn on to verify."
                    : "Automatically add credits when balance is low"}
              </p>
              {phaseLabel && (
                <p className="text-xs text-muted-foreground mt-1" aria-live="polite">{phaseLabel}</p>
              )}
            </div>
            <Switch
              aria-label="Auto-reload"
              checked={enabled}
              onCheckedChange={handleToggle}
              disabled={!hasSavedCard || isBusy || !canManage}
            />
          </div>
        </div>
      </div>

      {isIndia && hasSavedCard && (
        <div className="ml-14 rounded-xl border border-border/50 bg-muted/30 p-4 space-y-3 text-sm">
          <div className="flex items-start gap-3">
            <Landmark className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
            <p className="text-muted-foreground">
              Your card is issued in India. Under RBI rules your bank needs a one-time authorisation
              (e-mandate) before we can reload automatically, sends you a notice before each reload,
              and completes the charge about a day later.
            </p>
          </div>
          {mandateChip(mandateStatus, profile.payment_method_brand)}
        </div>
      )}

      {belowThresholdPrompt && (
        <Alert className="ml-14">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="space-y-3">
            <p>
              Your balance is below the threshold. The first automatic reload lands about a day
              after your next call.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setBelowThresholdPrompt(false);
                onAddFunds(profile.auto_reload_amount_credits);
              }}
            >
              Add funds now
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {hasSavedCard && canManage && (
        <div className="ml-14 space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="auto-reload-threshold" className="text-sm">Threshold</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="auto-reload-threshold"
                  aria-invalid={!!thresholdError}
                  aria-describedby={thresholdError ? "auto-reload-threshold-error" : undefined}
                  type="number"
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  min={THRESHOLD_MIN}
                  max={THRESHOLD_MAX}
                  step="0.01"
                  placeholder="20.00"
                  className="pl-7"
                />
              </div>
              {thresholdError && (
                <p id="auto-reload-threshold-error" className="text-sm text-destructive">{thresholdError}</p>
              )}
              <p className="text-xs text-muted-foreground">
                {!isIndia ? (
                  <>Between ${THRESHOLD_MIN} and ${THRESHOLD_MAX}</>
                ) : spend.hasData ? (
                  <>
                    Funds land about a day after the trigger. You spend about {formatMoney(spend.dailySpend)}/day
                    — keep the threshold above that.{" "}
                    <button
                      type="button"
                      className="font-medium underline underline-offset-2 hover:text-foreground"
                      onClick={() => setThreshold(String(suggestedThreshold))}
                    >
                      Use ${suggestedThreshold}
                    </button>
                  </>
                ) : (
                  <>Funds land about a day after the trigger — keep more than a day of usage above the threshold.</>
                )}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="auto-reload-amount" className="text-sm">Reload Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="auto-reload-amount"
                  aria-invalid={!!amountError}
                  aria-describedby={amountError ? "auto-reload-amount-error" : undefined}
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  min={AMOUNT_MIN}
                  max={cap}
                  step="0.01"
                  placeholder="50.00"
                  className="pl-7"
                />
              </div>
              {amountError && (
                <p id="auto-reload-amount-error" className="text-sm text-destructive">{amountError}</p>
              )}
              <p className="text-xs text-muted-foreground">
                {isIndia
                  ? `Up to $${cap} per reload for cards issued in India`
                  : `Between $${AMOUNT_MIN} and $${AMOUNT_MAX.toLocaleString()}`}
              </p>
            </div>
          </div>

          <Button onClick={handleSave} disabled={isBusy} size="sm" className="w-full sm:w-auto">
            {isBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isBusy ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      )}
    </div>
  );
}
